import { useMemo } from 'react';

import { usePermissions } from '../../contexts/PermissionContext';
import { useStateContext } from '../../contexts/ContextProvider';
import { useCoque } from '../shell/CoqueContext';

import {
    CLE_MAINTENANCE,
    CLE_MENAGE,
    CLE_MESSAGERIE,
    CLE_SUPPORT,
    ECRANS_MOBILES,
    PERMISSION_AFFECTER_MENAGE,
    estEcranEligible,
} from './mobileScreens';
import type { EcranMobile } from './mobileScreens';

/**
 * Les dérivations du registre — décision #17.
 *
 * **Quatre choses, une seule source.** Le panneau de navigation, les cartes de
 * repli, l'écran d'atterrissage et la garde de route sortent tous d'ici : c'est ce
 * qui interdit qu'ils divergent. Sur le web ils sont calculés à trois endroits, et
 * `defaultRouteOrder` prouve ce que ça coûte.
 *
 * Assemblage **côté client** de charges utiles qui sont déjà dans le navigateur :
 * `UserResource.permissions` via `PermissionProvider`, les `admin_has_*` via
 * `useStateContext()`. Exactement les mêmes objets que `DefaultLayout`.
 *
 * Pas d'endpoint `/api/mobile/navigation` : il serait une seconde implémentation
 * du test, côté back cette fois, à garder en phase avec le web à chaque évolution
 * de rôle ou de module. Et la navigation n'est pas la barrière de sécurité — la
 * barrière est le back sur chaque endpoint métier. Se tromper d'affichage est un
 * défaut d'ergonomie, pas une fuite.
 */
export interface Ecrans {
    /** Portés ∧ éligibles, dans l'ordre du registre. C'est la liste du panneau. */
    eligibles: EcranMobile[];
    /**
     * Non portés ∧ éligibles. Les cartes « consultable sur ordinateur » du
     * dashboard (#87), sous le MÊME test que les écrans portés.
     */
    replis: EcranMobile[];
    /**
     * Où l'on atterrit : le support pour un super admin, puis la messagerie si éligible, sinon « Maintenance » pour le
     * technicien, sinon « Mes chambres », sinon le premier écran porté éligible,
     * sinon `null` — c'est-à-dire la fin de course.
     */
    atterrissage: EcranMobile | null;
    /** Ce que consomme la garde de route. Le chemin, pas la clé : c'est l'URL qu'on tape. */
    estEligible: (chemin: string) => boolean;
    /**
     * L'établissement n'est pas encore connu.
     *
     * #27 : ne jamais rendre avant. L'axe module se lit sur l'établissement
     * sélectionné — rendre avant, c'est afficher un menu faux pendant une seconde,
     * puis le corriger sous le pouce de quelqu'un qui a déjà commencé à viser.
     */
    enAttente: boolean;
}

export function useEcrans(): Ecrans {
    const { hasPermission, isSuperAdmin } = usePermissions();
    const superAdmin = isSuperAdmin === true;
    const { currentFacility, isUserLoading } = useStateContext();

    /*
     * Le troisième axe, propre à l'écran « Commandes » (#40, #87) : il n'existe
     * pas d'`admin_has_orders`, donc son éligibilité se lit sur l'ÉQUIPEMENT —
     * « au moins un catalogue ». La règle elle-même vit dans
     * `domain/ecranCommandes.ts`, parce qu'elle gouverne aussi le module `orders`
     * du dashboard : une seule règle, deux surfaces.
     *
     * ⚠️ LU sur la coque, jamais requêté ici. Ce hook est un assemblage de charges
     * utiles déjà présentes dans le navigateur — c'est ce qui le rend testable sans
     * fournisseur, et une requête posée ici a effectivement cassé 21 tests avant
     * qu'on remette la donnée à sa place.
     */
    const { nombreDeCatalogues } = useCoque();

    return useMemo(() => {
        const enAttente = isUserLoading || currentFacility === null;

        const eligiblesTous = ECRANS_MOBILES.filter((ecran) =>
            estEcranEligible(ecran, hasPermission, superAdmin, currentFacility, nombreDeCatalogues),
        ).sort((a, b) => a.ordre - b.ordre);

        const eligibles = eligiblesTous.filter((ecran) => ecran.porte);
        const replis = eligiblesTous.filter((ecran) => !ecran.porte);

        // L'équipe support d'abord : sans ça un super admin atterrirait sur la
        // messagerie d'un hôtel, qu'il ne voit que par le contournement de `hasPermission`.
        const support = eligibles.find((ecran) => ecran.cle === CLE_SUPPORT);
        const messagerie = eligibles.find((ecran) => ecran.cle === CLE_MESSAGERIE);
        // Lot 3 Odomo : sans messagerie, le ménage passe avant le tableau de bord —
        // l'agent (et la gouvernante) ouvre l'app pour ses chambres, et c'est là
        // que mène son push. L'`ordre` du panneau n'a pas à porter cette priorité.
        const menage = eligibles.find((ecran) => ecran.cle === CLE_MENAGE);
        // Le technicien (Beaucour, 13/10) : la maintenance éligible SANS le droit
        // d'affecter les chambres. Il n'a aucune chambre à faire, « Mes chambres »
        // serait vide. La gouvernante, qui a les deux, garde « Mes chambres » ;
        // l'agent d'étage n'a pas la maintenance. Bypass admin = droit d'affecter,
        // donc atterrissage inchangé pour lui.
        const maintenance = eligibles.find((ecran) => ecran.cle === CLE_MAINTENANCE);
        const technicien = maintenance && !hasPermission(PERMISSION_AFFECTER_MENAGE) ? maintenance : undefined;
        const atterrissage = support ?? messagerie ?? technicien ?? menage ?? eligibles[0] ?? null;

        // La garde ne connaît QUE les écrans portés : un non porté n'a pas de route
        // à défendre, il n'est qu'une carte. Et `/diagnostic`, hors registre, est
        // monté hors garde — il ne prouve rien s'il dépend de ce qu'il diagnostique.
        const cheminsEligibles = new Set(eligibles.map((ecran) => ecran.chemin));

        return {
            eligibles,
            replis,
            atterrissage,
            // Un sous-chemin appartient à son écran : `/messaging/42` est la
            // messagerie. Sans ça, une notification ouvrant une conversation
            // précise se verrait refuser l'écran dont elle vient.
            estEligible: (chemin: string) =>
                [...cheminsEligibles].some(
                    (racine) => chemin === racine || chemin.startsWith(`${racine}/`),
                ),
            enAttente,
        };
    }, [hasPermission, superAdmin, currentFacility, isUserLoading, nombreDeCatalogues]);
}
